import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import { Button } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { useTranslation } from '@root/shared/localization'

import { TransferFeeCardEvm } from '../transfer-fee/transfer-fee-card'
import { TransferFeeCardSvm } from '../transfer-fee/transfer-fee-card-svm'

import { EstimatedChanges } from './estimated-changes'
import { TransferReviewHeader } from './transfer-review-header'
import { TransferTotals } from './transfer-totals'

import type { TransferFlowReatom } from '../../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: TransferFlowReatom
  onReject?: () => void
} & ClassNameProps

export const TransferReviewContent: React.FC<Props> = ({
  onReject,
  className,
  model,
}) => {
  const { t } = useTranslation()

  const [state] = useAtom(model.transferStateAtom)
  const [network] = useAtom(selectedNetworkAtom)
  const submit = useAction(model.executeTransferAction)

  const renderFeeCard = () =>
    match(network)
      .with(Network.Solana, () => (
        <TransferFeeCardSvm variant="dark" model={model.svmFeeModel} />
      ))
      .with(P.union(Network.Ethereum, Network.Base, Network.Bsc), () => (
        <TransferFeeCardEvm variant="dark" model={model.gasSettings} />
      ))
      .with(Network.Ton, () => <></>)
      .exhaustive()

  return (
    <Root className={className}>
      <TransferReviewHeader model={model} />
      <Blocks>
        <EstimatedChanges model={model} />
        {renderFeeCard()}
        <TransferTotals model={model} />
      </Blocks>
      <ActionContainer>
        <Button
          radius="rounded"
          fullWidth
          onClick={onReject}
          size="m"
          variant="secondary"
        >
          {t('common.close')}
        </Button>
        <Button
          radius="rounded"
          fullWidth
          busy={state.loading}
          onClick={submit}
          size="m"
          color="primary"
        >
          {t('common.confirm')}
        </Button>
      </ActionContainer>
    </Root>
  )
}

const Root = styled.div`
  padding-inline: 12px;
  min-width: 0;
`
const ActionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

const Blocks = styled.div`
  display: grid;
  gap: 6px;

  margin-bottom: 16px;
`
